import React from "react";
import { IndexForms } from "@hex-insights/app-modules";
import { Button, Column, Grid, Row, Rule } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	BooleanField,
	DateTimeField,
	FormType,
	Label,
	NumberField,
	SubFormField,
	SubFormFieldContentProps,
	SubFormFieldItemsProps,
	SubFormRenderProps,
	TextField,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { useDiscountSelectQuery, useServiceSelectQuery } from "../../../Utilities";
import { ContractLineItemFormValues } from "../../FormUtilities";
import { DiscountOrderFormValues, ServiceOrderFormValues } from "../../Order";
import { DiscountUtilities } from "../../Utilities";
import { ContractSelectField } from "../Contract";
import { ContractLineItemForm, ContractLineItemSelectField } from "../ContractLineItem";
import { DiscountSelectField } from "../Discount";
import { ServiceSelectField } from "../Service";
import { BaseFieldProps } from "../shared";
import styles from "./field.module.css";

/**
 * Generic props for fields of the ContractLineItem model.
 */
type FieldProps<K extends keyof ContractLineItemFormValues.Base = keyof ContractLineItemFormValues.Base> =
	BaseFieldProps<Pick<ContractLineItemFormValues.Base, K>>;

/**
 * Generic props for fields of the ContractLineItem model that only appear in the create form.
 */
type CreateFieldProps<K extends keyof ContractLineItemFormValues.Create = keyof ContractLineItemFormValues.Create> =
	BaseFieldProps<Pick<ContractLineItemFormValues.Create, K>>;

/**
 * Generic props for fields of the ContractLineItem model that only appear in the detail form.
 */
type DetailFieldProps<K extends keyof ContractLineItemFormValues.Detail = keyof ContractLineItemFormValues.Detail> =
	BaseFieldProps<Pick<ContractLineItemFormValues.Detail, K>>;

/**
 * Field component for the `displayOrder` field of the ContractLineItem model.
 */
export function DisplayOrder({ formState, ...props }: FieldProps<"displayOrder">) {
	return (
		<NumberField
			formState={formState}
			name="displayOrder"
			optional={FormType.isCreate(formState.formType)}
			validationUnit={1}
			{...props}
		/>
	);
}

/**
 * Field component for the `description` field of the ContractLineItem model.
 */
export function Description({ formState, ...props }: FieldProps<"description">) {
	return <TextField formState={formState} name="description" multiLine optional {...props} />;
}

/**
 * Field component for the `isDiscount` field of the ContractLineItem model.
 */
export function IsDiscount({ formState, ...props }: FieldProps<"isDiscount">) {
	return <BooleanField formState={formState} name="isDiscount" {...props} />;
}

/**
 * Field component for the `amount` field of the ContractLineItem model.
 */
export function Amount({ formState, ...props }: FieldProps<"amount">) {
	return <NumberField formState={formState} name="amount" validationUnit={1} min={0} unit={100} {...props} />;
}

/**
 * Field component for the `dateAdded` field of the ContractLineItem model.
 */
export function DateAdded({ formState, ...props }: FieldProps<"dateAdded">) {
	return <DateTimeField formState={formState} name="dateAdded" optional precision="day" {...props} />;
}

/**
 * Field component for the `dateRemoved` field of the ContractLineItem model.
 */
export function DateRemoved({ formState, ...props }: DetailFieldProps<"dateRemoved">) {
	return <DateTimeField formState={formState} name="dateRemoved" optional precision="day" {...props} />;
}

export namespace ChildContractLineItems {
	/**
	 * SubForm field component for the `childContractLineItems` edge of the ContractLineItem model.
	 */
	export function SubForm({ formState, style, ...props }: CreateFieldProps<"childContractLineItems" | "amount">) {
		const { amount } = formState.formValues;
		const SubFormSubForm = React.useCallback(
			(props: SubFormRenderProps<ContractLineItemFormValues.CreateInParentContractLineItem>) => {
				return <SubForm.SubForm {...props} basisPrice={amount} />;
			},
			[amount],
		);

		return (
			<SubFormField
				formState={formState}
				name="childContractLineItems"
				blankItem={ContractLineItemFormValues.blankItemInParentContractLineItem}
				formattedBlank=""
				Content={SubForm.Content}
				Items={SubForm.Items}
				dividerElement={<Rule />}
				style={{ width: "100%", "--sub-form-field__value---padding": 0, ...style } as React.CSSProperties}
				{...props}
			>
				{SubFormSubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	const gridTemplateTracks = "var(--general__field---width) 10rem 2rem";
	const gridGap = "1rem";

	SubForm.Content = function ({
		children,
	}: SubFormFieldContentProps<
		Pick<ContractLineItemFormValues.Create, "childContractLineItems" | "amount">,
		ContractLineItemFormValues.CreateInParentContractLineItem
	>) {
		return (
			<Column justify="spaced-start" className={styles["contract-line-items__content"]}>
				<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
					<Label>Discount</Label>
					<Label>Amount</Label>
				</Grid>

				<Rule />

				{children}
			</Column>
		);
	};

	SubForm.Items = function ({
		children,
	}: SubFormFieldItemsProps<
		Pick<ContractLineItemFormValues.Create, "childContractLineItems" | "amount">,
		ContractLineItemFormValues.CreateInParentContractLineItem
	>) {
		return <React.Fragment>{children}</React.Fragment>;
	};

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
		basisPrice,
	}: SubFormRenderProps<ContractLineItemFormValues.CreateInParentContractLineItem> & { basisPrice: number | null }) {
		return (
			<Grid templateTracks={gridTemplateTracks} gap={gridGap}>
				<ContractLineItemForm.CreateFieldsInParentContractLineItem formState={formState} basisPrice={basisPrice} />
				<Row justify="flex-end">
					<Button
						variant="tertiary"
						size="small"
						onClick={onRemoveClick}
						disabled={formState.disabled}
						className="button--icon"
					>
						<Icon.TrashSimple size={16} className="block" />
					</Button>
				</Row>
			</Grid>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
	}: AddSubFormButtonRenderProps<
		Pick<ContractLineItemFormValues.Create, "childContractLineItems" | "amount">,
		ContractLineItemFormValues.CreateInParentContractLineItem
	>) {
		return (
			<Button variant="link" size="small" onClick={onClick} disabled={disabled}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.Plus size={16} /> Add Discount
				</Row>
			</Button>
		);
	};
}

export namespace Contract {
	export type SelectProps = FieldProps<"contractID"> &
		Omit<ContractSelectField.SelectProps<ContractLineItemFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `contract` edge of the ContractLineItem model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <ContractSelectField.Select formState={formState} name="contractID" optional {...props} />;
	}
}

export namespace Discount {
	export type SelectProps = FieldProps<"discountID" | "amount"> &
		Omit<DiscountSelectField.SelectProps<ContractLineItemFormValues.Base>, "formState" | "name" | "optional"> & {
			basisPrice: number | null;
		};

	/**
	 * Field component for the `discount` edge of the ContractLineItem model.
	 */
	export function Select({ formState, queryOptions, basisPrice, ...props }: SelectProps) {
		usePricing(formState, queryOptions, basisPrice);

		return (
			<DiscountSelectField.Select formState={formState} name="discountID" queryOptions={queryOptions} {...props} />
		);
	}

	function usePricing(
		formState: SelectProps["formState"],
		queryOptions: SelectProps["queryOptions"],
		basisPrice: SelectProps["basisPrice"],
	) {
		const defaultOrder = IndexForms.useOrderInput(DiscountOrderFormValues.initial);
		const { data } = useDiscountSelectQuery({
			...queryOptions,
			variables: {
				...queryOptions?.variables,
				order: queryOptions?.variables?.order ?? defaultOrder,
			},
			skip: formState.formType === FormType.Update,
		});

		const {
			formValues: { discountID },
			formSetFunctions: { amount: setAmount },
		} = formState;
		React.useEffect(() => {
			if (formState.formType === FormType.Update) {
				return;
			}

			if (!discountID || basisPrice === null) {
				setAmount(null);
				return;
			}
			if (!data) {
				return;
			}
			for (let i = 0; i < data.discountConnection.edges.length; i++) {
				const discount = data.discountConnection.edges[i].node;
				if (discount.id === discountID) {
					setAmount(DiscountUtilities.calculateDiscountAmount(discount, basisPrice));
					return;
				}
			}
		}, [formState.formType, discountID, basisPrice, data, setAmount]);
	}
}

export namespace ParentContractLineItem {
	export type SelectProps = FieldProps<"parentContractLineItemID"> &
		Omit<ContractLineItemSelectField.SelectProps<ContractLineItemFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `parentContractLineItem` edge of the ContractLineItem model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return (
			<ContractLineItemSelectField.Select formState={formState} name="parentContractLineItemID" optional {...props} />
		);
	}
}

export namespace Service {
	export type SelectProps = FieldProps<"serviceID" | "amount"> &
		Omit<ServiceSelectField.SelectProps<ContractLineItemFormValues.Base>, "formState" | "name" | "optional">;

	/**
	 * Field component for the `service` edge of the ContractLineItem model.
	 */
	export function Select({ formState, queryOptions, ...props }: SelectProps) {
		usePricing(formState, queryOptions);

		return <ServiceSelectField.Select formState={formState} name="serviceID" queryOptions={queryOptions} {...props} />;
	}

	function usePricing(formState: SelectProps["formState"], queryOptions: SelectProps["queryOptions"]) {
		const defaultOrder = IndexForms.useOrderInput(ServiceOrderFormValues.initial);
		const { data } = useServiceSelectQuery({
			...queryOptions,
			variables: {
				...queryOptions?.variables,
				order: queryOptions?.variables?.order ?? defaultOrder,
			},
			skip: formState.formType === FormType.Update,
		});

		const {
			formValues: { serviceID },
			formSetFunctions: { amount: setAmount },
		} = formState;
		React.useEffect(() => {
			if (formState.formType === FormType.Update) {
				return;
			}

			if (!serviceID) {
				setAmount(null);
				return;
			}
			if (!data) {
				return;
			}
			for (let i = 0; i < data.serviceConnection.edges.length; i++) {
				const service = data.serviceConnection.edges[i].node;
				if (service.id === serviceID) {
					setAmount(service.price);
					return;
				}
			}
		}, [formState.formType, serviceID, data, setAmount]);
	}
}
